<template>
    <div class="bo-wrap h-full w-full flex flex-col text-left">
        <div class="bo-toolbar">
            <p class="bo-toolbar__hint">{{ entity }} · {{ campus }}</p>
            <div class="bo-toolbar__actions">
                <button type="button" class="bo-btn bo-btn--export" :disabled="isExportExcel || loading || !record"
                    @click="generateReport">
                    <font-awesome-icon :icon="['fas', 'file-excel']" class="bo-btn__icon bo-btn__icon--excel" />
                    <span>{{ isExportExcel ? "Exporting..." : "Export Excel" }}</span>
                </button>
                <button v-if="canCreate && !record" type="button" class="bo-btn bo-btn--edit" :disabled="loading"
                    @click="openCreate">
                    <font-awesome-icon icon="plus" class="bo-btn__icon" />
                    <span>Add</span>
                </button>
                <button v-if="canUpdate && record" type="button" class="bo-btn bo-btn--edit" :disabled="loading"
                    @click="openEdit">
                    <font-awesome-icon icon="pen" class="bo-btn__icon" />
                    <span>Edit</span>
                </button>
            </div>
        </div>

        <div class="bo-body flex-auto flex flex-col">
            <div v-if="loading" class="flex-auto flex justify-center items-center py-24">
                <base-page-spinner type="spin" class-list="h-16"></base-page-spinner>
            </div>

            <div v-else-if="!record" class="flex-auto flex justify-center items-center py-20">
                <div class="bo-empty">
                    <div class="bo-empty__glyph">
                        <font-awesome-icon :icon="['fas', 'building']" />
                    </div>
                    <h2 class="bo-empty__title">No building overview found</h2>
                    <p class="bo-empty__text">
                        No building overview record exists for this campus yet.
                    </p>
                    <button v-if="canCreate" type="button" class="bo-btn bo-btn--edit bo-empty__action"
                        @click="openCreate">
                        <font-awesome-icon icon="plus" class="bo-btn__icon" />
                        <span>Add building overview</span>
                    </button>
                </div>
            </div>

            <div v-else class="bo-content">
                <article class="bo-feature bo-feature--location bo-anim" style="--bo-delay: 0ms">
                    <div class="bo-feature__icon" aria-hidden="true">
                        <font-awesome-icon :icon="['fas', 'map-marker-alt']" />
                    </div>
                    <div class="bo-feature__copy min-w-0">
                        <p class="bo-feature__label">Location</p>
                        <p class="bo-feature__value">{{ record.location || "—" }}</p>
                    </div>
                </article>

                <div class="bo-metrics">
                    <article class="bo-feature bo-feature--age bo-anim" style="--bo-delay: 60ms">
                        <div class="bo-feature__icon" aria-hidden="true">
                            <font-awesome-icon :icon="['fas', 'hourglass-half']" />
                        </div>
                        <div class="bo-feature__copy min-w-0">
                            <p class="bo-feature__label">Building age</p>
                            <p class="bo-feature__value bo-feature__value--lg">
                                {{ formatBuildingAge(record.opened_on) }}
                            </p>
                        </div>
                    </article>

                    <article class="bo-feature bo-feature--floors bo-anim" style="--bo-delay: 120ms">
                        <div class="bo-feature__icon" aria-hidden="true">
                            <font-awesome-icon :icon="['fas', 'layer-group']" />
                        </div>
                        <div class="bo-feature__copy min-w-0">
                            <p class="bo-feature__label">Number of floors</p>
                            <p class="bo-feature__value bo-feature__value--lg">
                                {{ formatFloorsDisplay(record.floors_count) }}
                            </p>
                        </div>
                    </article>
                </div>

                <article class="bo-feature bo-feature--components bo-anim" style="--bo-delay: 180ms">
                    <div class="bo-feature__icon" aria-hidden="true">
                        <font-awesome-icon :icon="['fas', 'cubes']" />
                    </div>
                    <div class="bo-feature__copy min-w-0">
                        <p class="bo-feature__label">Components of the building</p>
                        <p class="bo-feature__value">{{ record.components || "—" }}</p>
                    </div>
                </article>
            </div>
        </div>

        <transition name="fade">
            <div v-if="showFormModal"
                class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
                @click.self="closeForm">
                <div class="bo-form bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
                    <div class="bo-form__header">
                        <h3 class="text-lg font-semibold text-gray-900">
                            {{ isCreating ? "Add building overview" : "Edit building overview" }}
                        </h3>
                        <p class="text-sm text-gray-500 mt-1">
                            {{ entity }} · {{ campus }}
                        </p>
                    </div>

                    <div class="bo-form__body grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label class="bo-label">Location <span class="text-red-500">*</span></label>
                            <input v-model="form.location" type="text" class="bo-input"
                                placeholder="Building location" />
                        </div>
                        <div>
                            <label class="bo-label">Opened on</label>
                            <input v-model="form.opened_on" type="date" class="bo-input" />
                            <p class="text-xs text-gray-500 mt-1">Used to calculate building age</p>
                        </div>
                        <div>
                            <label class="bo-label">Floors</label>
                            <input v-model="form.floors_count" type="number" min="0" class="bo-input"
                                placeholder="Number of floors" />
                        </div>
                        <div class="sm:col-span-2">
                            <label class="bo-label">Components</label>
                            <input v-model="form.components" type="text" class="bo-input"
                                placeholder="Building components or sections" />
                        </div>
                    </div>

                    <div class="bo-form__footer">
                        <button type="button" class="bo-btn bo-btn--ghost" @click="closeForm">
                            Cancel
                        </button>
                        <base-button type="button" :disabled="saving"
                            class="bg-amber-700 hover:bg-amber-800 text-white"
                            @click="saveForm">
                            {{ saving ? "Saving…" : "Save" }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";

const LIST_QUERY = gql`
    query FacilityBuildingOverviews(
        $first: Int!
        $page: Int!
        $entity: String
        $campus: String
    ) {
        facilityBuildingOverviews(
            first: $first
            page: $page
            entity: $entity
            campus: $campus
        ) {
            error
            error_code
            message
            data {
                id
                entity
                campus
                location
                opened_on
                components
                floors_count
                created_at
                updated_at
            }
        }
    }
`;

const CREATE_MUTATION = gql`
    mutation CreateFacilityBuildingOverview($input: FacilityBuildingOverviewInput!) {
        createFacilityBuildingOverview(input: $input) {
            error
            error_code
            message
        }
    }
`;

const UPDATE_MUTATION = gql`
    mutation UpdateFacilityBuildingOverview($id: ID!, $input: FacilityBuildingOverviewInput!) {
        updateFacilityBuildingOverview(id: $id, input: $input) {
            error
            error_code
            message
        }
    }
`;

function formatBuildingAge(openedOn) {
    if (!openedOn) return "—";
    var parts = String(openedOn).substring(0, 10).split("-");
    if (parts.length !== 3) return "—";
    var y = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10) - 1;
    var d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return "—";
    var opened = new Date(y, m, d);
    var now = new Date();
    var age = now.getFullYear() - opened.getFullYear();
    var md = now.getMonth() - opened.getMonth();
    if (md < 0 || (md === 0 && now.getDate() < opened.getDate())) age--;
    if (age < 0) age = 0;
    return age === 1 ? "1 year" : age + " years";
}

function emptyForm() {
    return {
        location: "",
        opened_on: "",
        components: "",
        floors_count: "",
    };
}

function toDateInputValue(value) {
    if (!value) return "";
    return String(value).substring(0, 10);
}

function parseFloorsCount(value) {
    if (value === "" || value === null || value === undefined) return null;
    var num = parseInt(value, 10);
    if (isNaN(num) || num < 0) return null;
    return num;
}

import exportManagerMixin from "./exportManagerMixin.js";

export default {
    name: "BuildingOverviewModule",
    mixins: [exportManagerMixin],
    props: {
        entity: { type: String, required: true },
        campus: { type: String, required: true },
    },
    emits: ["changed"],
    data() {
        return {
            record: null,
            loading: false,
            isExportExcel: false,
            exportSingleRecord: true,
            showFormModal: false,
            isCreating: false,
            saving: false,
            form: emptyForm(),
        };
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        permissions() {
            return this.$store.getters.permissions;
        },
        canCreate() {
            return this.hasPermission("facility-building-overview-create");
        },
        canUpdate() {
            return this.hasPermission("facility-building-overview-update");
        },
    },
    created() {
        this.loadRecord();
    },
    methods: {
        formatBuildingAge: formatBuildingAge,
        formatFloorsDisplay(value) {
            if (value === null || value === undefined || value === "") return "—";
            return String(value);
        },
        hasPermission(name) {
            if (this.role === "superadmin") return true;
            return this.permissions && this.permissions.includes(name);
        },
        toastAlert(options) {
            if (!this.$swal) return Promise.resolve({});
            var merged = Object.assign({ timer: 2000 }, options);
            if (
                (merged.icon === "error" || merged.icon === "warning") &&
                (options.timer === undefined || options.timer === null)
            ) {
                merged.timer = 3500;
            }
            var self = this;
            var Toast = this.$swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timerProgressBar: true,
                didOpen: function (toast) {
                    toast.addEventListener("mouseenter", self.$swal.stopTimer);
                    toast.addEventListener("mouseleave", self.$swal.resumeTimer);
                },
            });
            return Toast.fire(merged);
        },
        loadRecord() {
            var self = this;
            this.loading = true;
            this.$apollo
                .query({
                    query: LIST_QUERY,
                    variables: {
                        first: 1,
                        page: 1,
                        entity: this.entity,
                        campus: this.campus,
                    },
                    fetchPolicy: "network-only",
                })
                .then(function (result) {
                    var data = result && result.data ? result.data : null;
                    var payload =
                        data && data.facilityBuildingOverviews
                            ? data.facilityBuildingOverviews
                            : null;
                    if (payload && !payload.error) {
                        var rows = payload.data || [];
                        self.record = rows.length ? rows[0] : null;
                    } else {
                        self.record = null;
                        self.toastAlert({
                            icon: "error",
                            title:
                                (payload && payload.message) ||
                                "Failed to load building overview",
                        });
                    }
                })
                .catch(function (err) {
                    self.record = null;
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Failed to load building overview",
                    });
                })
                .finally(function () {
                    self.loading = false;
                });
        },
        openCreate() {
            if (this.record) return;
            this.isCreating = true;
            this.form = emptyForm();
            this.showFormModal = true;
        },
        openEdit() {
            if (!this.record) return;
            var item = this.record;
            this.isCreating = false;
            this.form = {
                location: item.location || "",
                opened_on: toDateInputValue(item.opened_on),
                components: item.components || "",
                floors_count:
                    item.floors_count === null || item.floors_count === undefined
                        ? ""
                        : item.floors_count,
            };
            this.showFormModal = true;
        },
        closeForm() {
            this.showFormModal = false;
            this.isCreating = false;
            this.form = emptyForm();
        },
        buildInput() {
            return {
                entity: this.entity,
                campus: this.campus,
                location: this.form.location || null,
                opened_on: this.form.opened_on || null,
                components: this.form.components || null,
                floors_count: parseFloorsCount(this.form.floors_count),
            };
        },
        saveForm() {
            var self = this;

            if (!this.isCreating && (!this.record || !this.record.id)) {
                this.toastAlert({ icon: "error", title: "No record selected to update" });
                return;
            }

            if (this.isCreating && this.record) {
                this.toastAlert({
                    icon: "warning",
                    title: "Building overview already exists for this campus",
                });
                return;
            }

            if (!this.form.location || !String(this.form.location).trim()) {
                this.toastAlert({ icon: "warning", title: "Location is required" });
                return;
            }

            this.saving = true;
            var input = this.buildInput();
            var request = this.isCreating
                ? this.$apollo.mutate({
                      mutation: CREATE_MUTATION,
                      variables: { input: input },
                  })
                : this.$apollo.mutate({
                      mutation: UPDATE_MUTATION,
                      variables: { id: this.record.id, input: input },
                  });

            request
                .then(function (res) {
                    var data = res && res.data ? res.data : null;
                    var payload = self.isCreating
                        ? data && data.createFacilityBuildingOverview
                            ? data.createFacilityBuildingOverview
                            : null
                        : data && data.updateFacilityBuildingOverview
                          ? data.updateFacilityBuildingOverview
                          : null;

                    if (payload && !payload.error) {
                        self.toastAlert({
                            icon: "success",
                            title: payload.message || "Saved successfully",
                        });
                        self.closeForm();
                        self.loadRecord();
                        self.$emit("changed", { refresh: true });
                    } else {
                        self.toastAlert({
                            icon: "error",
                            title: (payload && payload.message) || "Save failed",
                        });
                    }
                })
                .catch(function (err) {
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Save failed",
                    });
                })
                .finally(function () {
                    self.saving = false;
                });
        },
        exportFileName: function () {
            return "ADPOLY_" + this.campus + "_Building_Overview";
        },
        exportSheetName: function () {
            return "Building Overview";
        },
        buildExportRows: function () {
            if (!this.record) return [];
            return [{
                Location: this.record.location || "",
                "Building Age": formatBuildingAge(this.record.opened_on),
                Components: this.record.components || "",
                Floors:
                    this.record.floors_count === null ||
                    this.record.floors_count === undefined
                        ? ""
                        : this.record.floors_count,
            }];
        },
    },
};
</script>

<style lang="scss" scoped>
.bo-wrap {
    --bo-ink: #1c1917;
    --bo-muted: #78716c;
    --bo-line: #e7e5e4;
    --bo-amber: #b45309;
    --bo-amber-soft: #fff7ed;
    --bo-slate: #0f766e;
    background:
        radial-gradient(1200px 420px at 10% -10%, rgba(251, 191, 36, 0.16), transparent 55%),
        radial-gradient(900px 380px at 100% 0%, rgba(15, 118, 110, 0.08), transparent 50%),
        linear-gradient(180deg, #fafaf9 0%, #ffffff 48%);
}

.bo-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1.15rem 1.25rem 0.35rem;
}

@media (min-width: 640px) {
    .bo-toolbar {
        padding-left: 2rem;
        padding-right: 2rem;
    }
}

.bo-toolbar__hint {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--bo-muted);
}

.bo-toolbar__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
}

.bo-body {
    padding: 0.75rem 1.25rem 2rem;
}

@media (min-width: 640px) {
    .bo-body {
        padding-left: 2rem;
        padding-right: 2rem;
    }
}

.bo-content {
    max-width: 56rem;
    margin: 0 auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.bo-metrics {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
}

@media (min-width: 640px) {
    .bo-metrics {
        grid-template-columns: 1fr 1fr;
    }
}

.bo-feature {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem 1.35rem;
    border-radius: 1.15rem;
    border: 1px solid var(--bo-line);
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 1px 2px rgba(28, 25, 23, 0.04);
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.bo-feature:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(28, 25, 23, 0.08);
    border-color: #d6d3d1;
}

.bo-feature__icon {
    flex-shrink: 0;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.9rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.05rem;
    color: #fff;
}

.bo-feature--location .bo-feature__icon {
    background: linear-gradient(145deg, #d97706, #b45309);
}

.bo-feature--age .bo-feature__icon {
    background: linear-gradient(145deg, #0d9488, #0f766e);
}

.bo-feature--floors .bo-feature__icon {
    background: linear-gradient(145deg, #57534e, #44403c);
}

.bo-feature--components .bo-feature__icon {
    background: linear-gradient(145deg, #ca8a04, #a16207);
}

.bo-feature--location,
.bo-feature--components {
    background: linear-gradient(135deg, rgba(255, 247, 237, 0.85), rgba(255, 255, 255, 0.95));
}

.bo-feature__label {
    margin: 0;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--bo-muted);
}

.bo-feature__value {
    margin: 0.35rem 0 0;
    font-size: 1.05rem;
    font-weight: 650;
    line-height: 1.45;
    color: var(--bo-ink);
    word-break: break-word;
}

.bo-feature__value--lg {
    font-size: 1.55rem;
    font-weight: 750;
    letter-spacing: -0.02em;
    line-height: 1.2;
}

.bo-anim {
    animation: boRise 0.45s ease both;
    animation-delay: var(--bo-delay, 0ms);
}

@keyframes boRise {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.bo-empty {
    text-align: center;
    max-width: 22rem;
    padding: 0 1rem;
}

.bo-empty__glyph {
    width: 3.5rem;
    height: 3.5rem;
    margin: 0 auto 1rem;
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bo-amber-soft);
    color: var(--bo-amber);
    font-size: 1.35rem;
}

.bo-empty__title {
    margin: 0 0 0.4rem;
    font-size: 1.15rem;
    font-weight: 700;
    color: var(--bo-ink);
}

.bo-empty__text {
    margin: 0;
    color: var(--bo-muted);
    font-size: 0.95rem;
    line-height: 1.5;
}

.bo-empty__action {
    margin-top: 1.25rem;
}

.bo-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    border-radius: 0.75rem;
    padding: 0.6rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.bo-btn:disabled {
    pointer-events: none;
    opacity: 0.55;
}

.bo-btn__icon {
    font-size: 0.85rem;
}

.bo-btn__icon--excel {
    color: #059669;
}

.bo-btn--export {
    border: 1px solid rgba(16, 185, 129, 0.28);
    background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
    color: #052e1f;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.bo-btn--export:hover {
    border-color: rgba(16, 185, 129, 0.45);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.12);
}

.bo-btn--edit {
    border: 1px solid transparent;
    background: linear-gradient(145deg, #d97706, #b45309);
    color: #fff;
    box-shadow: 0 4px 12px rgba(180, 83, 9, 0.22);
}

.bo-btn--edit:hover {
    filter: brightness(1.05);
}

.bo-btn--ghost {
    background: #ffffff;
    color: #44403c;
    border: 1px solid var(--bo-line);
}

.bo-btn--ghost:hover {
    background: #fafaf9;
}

.bo-input {
    width: 100%;
    border: 1px solid #d6d3d1;
    border-radius: 0.65rem;
    padding: 0.55rem 0.8rem;
    font-size: 0.875rem;
    background: #ffffff;
    color: var(--bo-ink);
}

.bo-input:focus {
    outline: none;
    border-color: #d97706;
    box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.15);
}

.bo-form__header {
    padding: 1.25rem 1.5rem 0.85rem;
    border-bottom: 1px solid #f5f5f4;
}

.bo-form__body {
    padding: 1.25rem 1.5rem;
}

.bo-form__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem 1.5rem 1.25rem;
    border-top: 1px solid #f5f5f4;
}

.bo-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: #44403c;
    margin-bottom: 0.35rem;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
